import { useEffect, useRef, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "../style/ImageCarousel.css";

import photo02 from "./images/Erfolg_Kand/PHOTO-2026-09-28-15-20-28.jpg";
import photo03 from "./images/Erfolg_Kand/PHOTO-2026-09-28-15-20-38.jpg";
import photo06 from "./images/Erfolg_Kand/PHOTO-2026-09-28-15-21-09 4.jpg";
import photo07 from "./images/Erfolg_Kand/PHOTO-2026-09-28-15-21-09 5.jpg";
import photo08 from "./images/Erfolg_Kand/PHOTO-2026-09-28-15-21-09 6.jpg";
import photo12 from "./images/Erfolg_Kand/PHOTO-2026-09-28-15-21-09.jpg";
import photo14 from "./images/Erfolg_Kand/PHOTO-2026-09-28-15-21-10.jpg";
import photo15 from "./images/Erfolg_Kand/PHOTO-2026-09-28-15-21-11 2.jpg";

const photos = [
  { src: photo06, alt: "Absolventinnen und Absolventen bei der Abschlussfeier" },
  { src: photo03, alt: "Absolventin bei der Zertifikatsübergabe" },
  { src: photo02, alt: "Absolventin mit ihrem Ausbildungszertifikat" },
  { src: photo07, alt: "Absolventin mit Zertifikat bei der Abschlussfeier" },
  { src: photo08, alt: "Absolventin mit Zertifikat und Medway-Geschenktüte" },
  { src: photo12, alt: "Absolventin mit ihrem Zertifikat" },
  { src: photo14, alt: "Absolventin mit Zertifikat vor dem Medway-Banner" },
  { src: photo15, alt: "Beratung zu Unterlagen und nächsten Schritten" },
];

const carouselSlides = [photos[photos.length - 1], ...photos, photos[0]];

function ImageCarousel() {
  const [slideIndex, setSlideIndex] = useState(1);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [touching, setTouching] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const touchStartX = useRef(null);
  const paused = hovered || touching;

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotionPreference = () => setReducedMotion(mediaQuery.matches);
    updateMotionPreference();
    mediaQuery.addEventListener("change", updateMotionPreference);

    return () => mediaQuery.removeEventListener("change", updateMotionPreference);
  }, []);

  useEffect(() => {
    if (paused) return undefined;

    const intervalId = window.setInterval(() => {
      setSlideIndex((currentIndex) => {
        if (reducedMotion) return (currentIndex % photos.length) + 1;
        return currentIndex + 1;
      });
    }, 2000);

    return () => window.clearInterval(intervalId);
  }, [paused, reducedMotion]);

  const goToSlide = (photoIndex) => {
    setTransitionEnabled(true);
    setSlideIndex(photoIndex + 1);
  };

  const moveBy = (direction) => {
    setTransitionEnabled(true);
    setSlideIndex((currentIndex) => {
      if (reducedMotion) {
        const currentPhotoIndex = (currentIndex - 1 + photos.length) % photos.length;
        return ((currentPhotoIndex + direction + photos.length) % photos.length) + 1;
      }

      return currentIndex + direction;
    });
  };

  const handleTransitionEnd = (event) => {
    if (event.target !== event.currentTarget) return;

    if (slideIndex === 0 || slideIndex === photos.length + 1) {
      setTransitionEnabled(false);
      setSlideIndex(slideIndex === 0 ? photos.length : 1);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setTransitionEnabled(true));
      });
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      moveBy(-1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      moveBy(1);
    }
  };

  const handlePointerDown = (event) => {
    if (event.pointerType !== "touch") return;
    touchStartX.current = event.clientX;
    setTouching(true);
  };

  const handlePointerUp = (event) => {
    if (touchStartX.current === null) return;
    const swipeDistance = event.clientX - touchStartX.current;

    if (Math.abs(swipeDistance) > 45) {
      moveBy(swipeDistance > 0 ? -1 : 1);
    }

    touchStartX.current = null;
    setTouching(false);
  };

  const handlePointerCancel = () => {
    touchStartX.current = null;
    setTouching(false);
  };

  const currentPhotoIndex = (slideIndex - 1 + photos.length) % photos.length;

  return (
    <section
      className="medway-album"
      aria-label="Erfolgsgeschichten in Bildern"
      aria-roledescription="Karussell"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse") setHovered(true);
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse") setHovered(false);
      }}
    >
      <div className="medway-album__heading">
        <p>Einblicke aus unserem Alltag</p>
        <h2>Gemeinsam erfolgreich</h2>
        <span>
          Von der ersten Beratung bis zum erfolgreichen Abschluss – Medway begleitet Sie auf Ihrem Weg. 🎓✨
          <br />
          <strong>Medway – Kompetente Beratung. Starke Ausbildung. Erfolgreiche Zukunft.</strong>
        </span>
      </div>

      <div className="medway-album__viewport">
        <div
          className={`medway-album__track${transitionEnabled && !reducedMotion ? " is-animated" : ""}`}
          style={{ transform: `translateX(-${slideIndex * 100}%)` }}
          onTransitionEnd={handleTransitionEnd}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onLostPointerCapture={handlePointerCancel}
        >
          {carouselSlides.map((photo, index) => (
            <div className="medway-album__slide" key={`${photo.src}-${index}`} aria-hidden={
              index !== slideIndex
            }>
              <img src={photo.src} alt={photo.alt} loading="lazy" decoding="async" draggable="false" />
            </div>
          ))}
        </div>
        <button
          className="medway-album__arrow medway-album__arrow--previous"
          type="button"
          aria-label="Vorheriges Bild"
          onClick={() => moveBy(-1)}
        >
          <FaChevronLeft aria-hidden="true" />
        </button>
        <button
          className="medway-album__arrow medway-album__arrow--next"
          type="button"
          aria-label="Nächstes Bild"
          onClick={() => moveBy(1)}
        >
          <FaChevronRight aria-hidden="true" />
        </button>
      </div>

      <div className="medway-album__controls">
        <div className="medway-album__dots" aria-label="Bild auswählen">
          {photos.map((photo, index) => (
            <button
              key={photo.src}
              type="button"
              aria-label={`Bild ${index + 1} von ${photos.length} anzeigen`}
              aria-current={currentPhotoIndex === index ? "true" : undefined}
              className={currentPhotoIndex === index ? "is-current" : ""}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default ImageCarousel;
